const CACHE_TTL = 300_000; // 5 minutes
const cache = new Map<string, { data: WeatherResult; expires: number }>();

export interface WeatherResult {
  temperature: number;
  weatherCode: number;
}

// NOTE: every name here MUST exist in @iconify-json/solar — names that don't
// (previously cloud-drizzle-bold / cloud-snow-bold) render nothing. Solar has
// no drizzle glyph: drizzle codes share cloud-rain-bold; snow uses
// cloud-snowfall-bold.
const WMO_ICON_MAP: Record<string, string> = {
  '0': 'solar:sun-bold',
  '1': 'solar:cloud-sun-bold',
  '2': 'solar:cloud-sun-bold',
  '3': 'solar:clouds-bold',
  '45': 'solar:fog-bold',
  '48': 'solar:fog-bold',
  '51': 'solar:cloud-rain-bold',
  '53': 'solar:cloud-rain-bold',
  '55': 'solar:cloud-rain-bold',
  '56': 'solar:cloud-rain-bold',
  '57': 'solar:cloud-rain-bold',
  '61': 'solar:cloud-rain-bold',
  '63': 'solar:cloud-rain-bold',
  '65': 'solar:cloud-rain-bold',
  '66': 'solar:cloud-snowfall-bold',
  '67': 'solar:cloud-snowfall-bold',
  '71': 'solar:cloud-snowfall-bold',
  '73': 'solar:cloud-snowfall-bold',
  '75': 'solar:cloud-snowfall-bold',
  '77': 'solar:cloud-snowfall-bold',
  '80': 'solar:cloud-rain-bold',
  '81': 'solar:cloud-rain-bold',
  '82': 'solar:cloud-rain-bold',
  '85': 'solar:cloud-snowfall-bold',
  '86': 'solar:cloud-snowfall-bold',
  '95': 'solar:cloud-storm-bold',
  '96': 'solar:cloud-storm-bold',
  '99': 'solar:cloud-storm-bold',
};

export function getWeatherIcon(weatherCode: number): string {
  return WMO_ICON_MAP[String(weatherCode)] || 'solar:question-circle-bold';
}

const OPEN_METEO_URL = 'https://api.open-meteo.com/v1/forecast';
const GEOCODE_URL = 'https://geocoding-api.open-meteo.com/v1/search';

const geocodeCache = new Map<string, { lat: number; lon: number } | null>();

/**
 * Resolve a place name ("A Coruña, Spain") to coordinates via Open-Meteo's
 * free geocoding API. N8N leads usually carry null lat/lon, so weather needs
 * this city-level fallback. Cached per query string.
 */
export async function geocodePlace(query: string): Promise<{ lat: number; lon: number } | null> {
  const key = query.trim().toLowerCase();
  if (!key) return null;
  if (geocodeCache.has(key)) return geocodeCache.get(key) ?? null;

  try {
    const response = await fetch(`${GEOCODE_URL}?name=${encodeURIComponent(key)}&count=1&language=en&format=json`);
    if (!response.ok) return null;
    const json = await response.json();
    const hit = json?.results?.[0];
    const result = hit && Number.isFinite(hit.latitude) && Number.isFinite(hit.longitude)
      ? { lat: hit.latitude, lon: hit.longitude }
      : null;
    geocodeCache.set(key, result);
    return result;
  } catch {
    geocodeCache.set(key, null);
    return null;
  }
}

export async function fetchWeather(lat: number, lon: number): Promise<WeatherResult | null> {
  const key = `${lat.toFixed(2)},${lon.toFixed(2)}`;
  const cached = cache.get(key);
  if (cached && cached.expires > Date.now()) {
    return cached.data;
  }

  try {
    const url = `${OPEN_METEO_URL}?latitude=${lat}&longitude=${lon}&current=temperature_2m,weather_code&timezone=auto`;
    const response = await fetch(url);
    if (!response.ok) return null;

    const json = await response.json();
    const current = json.current;
    if (!current || current.temperature_2m === undefined || current.weather_code === undefined) return null;

    const result: WeatherResult = {
      temperature: Math.round(current.temperature_2m),
      weatherCode: current.weather_code,
    };

    cache.set(key, { data: result, expires: Date.now() + CACHE_TTL });
    return result;
  } catch {
    return null;
  }
}